import { useCallback, useEffect, useRef, useState, type ReactNode, type Ref } from 'react'
import { FetchingProgress } from './fetching-progress'
import { useIsCollapsing, useIsInCollapsibleListPanel } from './layout'
import { useOptionalCollapsiblePanel } from './collapsible-panel'
import { cn } from '@/lib/utils'

/**
 * Vertical pill indicator at the left edge of the selected item.
 *
 * Rendered as a child of the scrolling container, so scroll moves it with the
 * content naturally — no per-scroll math. `top` is `offsetTop`-based and only
 * recomputed on selection/layout changes.
 *
 * Outer div: compositor-only translateY — animates smoothly between selections.
 * Inner div: scaleY grow animation on first appearance.
 */
function CollapsiblePill(props: { top: number; phase: 'grow' | 'visible' }) {
  return (
    <div
      className="pointer-events-none absolute top-0 left-0 z-10 will-change-transform"
      style={{
        transform: `translateY(${props.top}px)`,
        transition: props.phase === 'visible' ? 'transform 180ms cubic-bezier(0.4,0,0.2,1)' : undefined,
      }}
    >
      <div
        className={cn(
          'w-1 h-5 rounded-r-full bg-foreground origin-center',
          props.phase === 'grow' && 'animate-[pill-grow_250ms_ease-out_forwards]',
        )}
      />
    </div>
  )
}

export function ScrollableList(props: {
  children: ReactNode
  isFetching?: boolean
  isFetchingMore?: boolean
  loadMoreRef?: Ref<HTMLDivElement>
  emptyState?: ReactNode
  isEmpty?: boolean
  hint?: ReactNode
}) {
  const isCollapsing = useIsCollapsing()
  const isInListPanel = useIsInCollapsibleListPanel()
  const optionalPanel = useOptionalCollapsiblePanel()
  // Pill shows in lists that live inside a CollapsiblePanel but NOT inside a
  // CollapsibleListPanel — i.e. the middle/secondary panel, not the left rail.
  // The selection pill is always-on there as long as something is selected.
  const shouldShowPill = !!optionalPanel && !isInListPanel
  const containerRef = useRef<HTMLDivElement>(null)
  const [pillTop, setPillTop] = useState<number | null>(null)
  const [pillPhase, setPillPhase] = useState<'hidden' | 'grow' | 'visible'>('hidden')
  const phaseTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const rafRef = useRef<number>(0)

  // Selected item's vertical center, in the container's content coordinate space.
  //
  // We sum `offsetTop` up the offsetParent chain to the scroll container rather than
  // using getBoundingClientRect. offsetTop is layout-based and ignores CSS transforms,
  // so the pill lands on the item's FINAL resting position even while AnimatedList
  // (auto-animate) is mid-reorder/entrance animation — which it drives with transforms.
  // getBoundingClientRect would capture the transient transformed position: on a
  // re-sort the MutationObserver fires while items are still translated to their old
  // spots, so the pill would measure the old position and stay stuck pointing at
  // whatever row now sits there. Walking the chain (instead of a single offsetTop)
  // keeps it correct through intermediate positioned wrappers like AnimatedList, and
  // the result is already in the container's content coords (scroll-independent) —
  // exactly the space the pill, a child of the scrolling container, is positioned in.
  // react-doctor-disable-next-line react-doctor/react-compiler-no-manual-memoization -- kept stable: consumed in a useEffect dep array below; removing it trips react-hooks/exhaustive-deps (a project lint error)
  const measureSelected = useCallback((): number | null => {
    const container = containerRef.current
    if (!container) return null
    const selected = container.querySelector('[data-selected]') as HTMLElement | null
    if (!selected) return null
    let top = 0
    let el: HTMLElement | null = selected
    while (el && el !== container) {
      top += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }
    // Chain didn't reach the container (it wasn't an offsetParent) — bail rather than
    // render the pill at a bogus position.
    if (el !== container) return null
    return top + (selected.offsetHeight - 20) / 2
  }, [])

  // Drive the pill from selection presence: it animates in when an item gains
  // [data-selected], slides as the selection moves, and hides as soon as nothing
  // is selected (e.g. switching offers swaps the applicants list and the previous
  // [data-selected] item leaves the DOM).
  // react-doctor-disable-next-line react-hooks-js/set-state-in-effect -- legit external sync: drives the selection pill from DOM measurements (rAF) + [data-selected] presence, not derivable during render
  useEffect(() => {
    if (!shouldShowPill) {
      clearTimeout(phaseTimerRef.current)
      cancelAnimationFrame(rafRef.current)
      setPillPhase('hidden')
      setPillTop(null)
      return
    }

    const container = containerRef.current
    if (!container) return

    const updatePill = () => {
      const top = measureSelected()
      if (top !== null) {
        setPillTop(top)
        setPillPhase((prev) => {
          if (prev !== 'hidden') return prev
          clearTimeout(phaseTimerRef.current)
          phaseTimerRef.current = setTimeout(() => setPillPhase('visible'), 250)
          return 'grow'
        })
      } else {
        clearTimeout(phaseTimerRef.current)
        setPillPhase('hidden')
        setPillTop(null)
      }
    }

    rafRef.current = requestAnimationFrame(updatePill)

    // ResizeObserver on direct children catches layout shifts that don't trigger
    // mutations — e.g. the SelectionHint collapsing via a 250ms grid-rows transition
    // pushes items up while [data-selected] stays put, and without this the pill
    // would stick at its initially-measured offsetTop.
    const resizeObserver = new ResizeObserver(updatePill)
    for (const child of Array.from(container.children)) {
      resizeObserver.observe(child)
    }

    // childList catches list swaps (offer change → new applicants); attributeFilter
    // catches selection moving between siblings.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'childList') continue
        for (const node of m.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE && node.parentNode === container) {
            resizeObserver.observe(node as Element)
          }
        }
      }
      updatePill()
    })
    observer.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-selected'],
    })

    return () => {
      observer.disconnect()
      resizeObserver.disconnect()
      cancelAnimationFrame(rafRef.current)
    }
  }, [shouldShowPill, measureSelected])

  useEffect(() => {
    return () => {
      clearTimeout(phaseTimerRef.current)
      cancelAnimationFrame(rafRef.current)
    }
  }, [])

  const showPill = pillPhase !== 'hidden' && pillTop !== null
  const pillElement = showPill ? <CollapsiblePill top={pillTop ?? 0} phase={pillPhase} /> : null

  return (
    <div
      ref={containerRef}
      className={cn(
        // `relative` makes this the containing block for the absolutely-positioned pill.
        // Condition uses `isCollapsing` (immediate target) so on expand the padding
        // grows back from px-2 to p-3 in sync with the width transition (0..250ms).
        // overflow-anchor:none prevents the browser's scroll-anchoring algorithm from
        // scrolling the hint off-screen when it grows from 0→full height on initial mount.
        'relative hide-scrollbar flex flex-1 flex-col gap-1 overflow-auto p-3 transition-[padding] duration-250 ease-in-out [overflow-anchor:none]',
        isCollapsing && 'px-2',
      )}
    >
      {pillElement}
      {props.isEmpty && props.emptyState ? (
        props.emptyState
      ) : (
        <>
          <FetchingProgress isFetching={props.isFetching || props.isFetchingMore} className="-top-3 -mx-3 -mt-3 mb-1" />
          {props.hint}
          {props.children}
          {props.loadMoreRef && <div ref={props.loadMoreRef} />}
        </>
      )}
    </div>
  )
}
